import {
  isChrome,
  isEdge,
  isFirefox,
  isMobileSafari,
  isSafari,
  isMacOs,
  isIOS
} from 'react-device-detect';
import { Tracker } from 'services/tracking';
import { getHostedPageFlowType } from 'utilities/global';
import {
  GA_CATEGORIES,
  BROWSER_MEDIA_PERMISSION_ERROR_TYPE,
  CUSTOM_MEDIA_PERMISSION_ERROR_TYPE
} from 'consts';

export const requestAudioPermissionsFunc = tracker =>
  requestMediaPermissions({ audio: true, video: false }, tracker);
export const requestVideoPermissionsFunc = tracker =>
  requestMediaPermissions({ audio: false, video: true }, tracker);
export const requestBothPermissionsFunc = tracker =>
  requestMediaPermissions({ audio: true, video: true }, tracker);

const requestMediaPermissions = (constraints, customTracker) => {
  const tracker = customTracker || new Tracker(getHostedPageFlowType());

  return new Promise(resolve => {
    const isSafariBrowser = isSafari || isMobileSafari;

    if (!navigator.mediaDevices?.getUserMedia) {
      const errorType =
        (isMacOs || isIOS) && !isSafariBrowser
          ? CUSTOM_MEDIA_PERMISSION_ERROR_TYPE.UserMediaNotAvailable
          : BROWSER_MEDIA_PERMISSION_ERROR_TYPE.Generic;
      resolve({
        isSupported: false,
        error: {
          type: errorType
        }
      });
      return;
    }

    navigator.mediaDevices
      .getUserMedia(constraints ?? { audio: true, video: true })
      .then(stream => {
        stream.getTracks().forEach(t => {
          t.stop();
        });
        resolve({
          isSupported: true,
          error: null
        });
      })
      .catch(err => {
        const errName = err.name;
        const errMessage = err.message;
        let errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.Generic;
        if (isChrome) {
          if (errName === 'NotAllowedError') {
            if (errMessage === 'Permission denied by system') {
              errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.SystemPermissionDenied;
            } else if (
              [
                'Permission denied',
                'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission'
              ].some(errorText => errMessage.startsWith(errorText))
            ) {
              errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.UserPermissionDenied;
            }
          } else if (errName === 'NotReadableError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.CouldNotStartVideoSource;
          } else if (errName === 'NotFoundError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.NotFound;
          }
        } else if (isSafariBrowser) {
          if (errName === 'OverconstrainedError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.NotFound;
          } else if (errName === 'NotAllowedError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.UserPermissionDenied;
          }
        } else if (isEdge) {
          if (errName === 'NotAllowedError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.UserPermissionDenied;
          } else if (errName === 'NotReadableError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.CouldNotStartVideoSource;
          } else if (errName === 'NotFoundError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.NotFound;
          }
        } else if (isFirefox) {
          // https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
          if (errName === 'NotFoundError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.NotFound;
          } else if (errName === 'NotReadableError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.SystemPermissionDenied;
          } else if (errName === 'NotAllowedError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.UserPermissionDenied;
          } else if (errName === 'AbortError') {
            errorType = BROWSER_MEDIA_PERMISSION_ERROR_TYPE.CouldNotStartVideoSource;
          }
        }

        tracker.fireEvent({
          category: GA_CATEGORIES.CAMERA_ERROR,
          action: GA_CATEGORIES.CAMERA_ERROR,
          label: errorType
        });
        resolve({
          isSupported: false,
          error: {
            type: errorType,
            name: err.name,
            message: err.message
          }
        });
      });
  });
};
